import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Check, Save } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Btn } from "../../components/shared/Btn";
import { ConfirmDialog } from "../../components/shared/ConfirmDialog";
import { useAppData } from "../../lib/AppDataContext";
import { Card } from "../../components/shared/Card";
import { toast } from "../../components/shared/Toast";
import { slugify } from "../../lib/format";
import { propertiesApi, possessionDateFromParts } from "../../lib/propertiesApi";
import { seoApi } from "../../lib/seoApi";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { ConfigsStep } from "./steps/ConfigsStep";
import { MediaStep } from "./steps/MediaStep";
import { AmenitiesStep } from "./steps/AmenitiesStep";
import { SpecialOfferStep } from "./steps/SpecialOfferStep";
import { LocationStep } from "./steps/LocationStep";
import { SeoStep } from "./steps/SeoStep";

/* Property add/edit wizard. Owns the full draft, saves via propertiesApi
   directly so it gets the saved record back (id + slug), then attaches the
   uploaded cover/gallery images as PropertyImage rows and upserts the
   per-route SEO metadata. onSaved() tells PropertiesView to refresh+close. */
const STEPS = [
  { id: "basic",    label: "Basic Info",     component: BasicInfoStep },
  { id: "configs",  label: "Configurations", component: ConfigsStep },
  { id: "media",    label: "Media",          component: MediaStep },
  { id: "amenities", label: "Amenities",     component: AmenitiesStep },
  { id: "offer",    label: "Special Offer",  component: SpecialOfferStep },
  { id: "location", label: "Location",       component: LocationStep },
  { id: "seo",      label: "SEO",            component: SeoStep },
];

const emptyProperty = () => ({
  title: "", slug: "", builderId: "", localityId: "", city: "Pune",
  propertyType: "Apartment", status: "draft", possessionStatus: "under_construction", reraVerified: false, zeroBrokerage: false, reraId: "", possessionDate: "",
  shortDescription: "", fullDescription: "", aboutHeading: "",
  landParcel: "", towers: "", floors: "", totalUnits: "",
  parking: "", totalAmenities: "", vastu: "", reraPossession: "", litigation: "", naPlot712: "", gatedCommunity: "", servantRoom3plus: "",
  coverImage: null, gallery: [], floorPlans: [],
  configs: [],
  amenities: [],
  specialOffer: { enabled: false, badge: "", title: "", discount: "", description: "", validTill: "", youtubeUrl: "" },
  location: { address: "", lat: "", lng: "", nearby: [] },
  seo: { metaTitle: "", metaDescription: "", focusKeyword: "" },
});

// Remap a saved property (toUiProperty shape) back into the wizard's
// field names, so editing shows the saved data instead of blanks.
function fromUi(p) {
  if (!p) return null;
  const amenities = (p.amenities || []).map((a) => (typeof a === "string" ? a : a?.title || a?.name || "")).filter(Boolean);
  const configs = (p.bhkConfigs || []).map((c, i) => ({
    id: "cfg_load_" + i,
    bhk: c.config || "",
    servantRoom: c.servantRoom ?? false,
    carpetArea: c.area ? String(c.area).replace(/[^0-9.]/g, "") : "",
    builtUp: c.builtUp || "",
    areaUnit: "sq_ft",
    price: c.price != null && c.price !== "" ? String(c.price) : (c.from ? String(c.from).replace(/[^0-9.]/g, "") : ""),
    priceLabel: c.priceLabel || c.from || "",
    units: c.units != null && c.units !== "" ? String(c.units) : "",
    floorPlan: c.floorPlan || "",
  }));
  const imgs = p.images || [];
  const coverImg = imgs.find((im) => im.category === "cover") || imgs[0] || (p.thumbnail ? { url: p.thumbnail } : null);
  // Every image except the cover, regardless of category.
  //
  // Filtering on category === "gallery" hid most images: repeated saves wrote
  // extra "cover" rows (Runwal Crown 10, Skyline Vista 15) and some older rows
  // have no category. The customer site reads images[] unfiltered, so it showed
  // all of them while the wizard showed a fraction — with no way to delete the
  // ones you could not see.
  //
  // Comparing by url, not object identity: coverImg may be a synthesised
  // { url: p.thumbnail } rather than an element of imgs.
  const gallery = imgs
    .filter((im) => im.url && im.url !== coverImg?.url)
    .map((im) => ({ url: im.url, name: im.altText || "" }));
  const nearby = (p.nearby || []).map((n, i) => ({
    id: "n_load_" + i,
    name: n.place || n.name || "",
    type: n.type || "Office Hub",
    distance: n.distance || "",
  }));
  return {
    title: p.name || p.title || "",
    slug: p.slug || "",
    builderId: p.builderId || "",
    localityId: p.localityId || "",
    city: p.city || "Pune",
    propertyType: ({
      apartment: "Apartment", villa: "Villa", plot: "Plot", duplex: "Duplex",
      studio: "Studio Apartment", commercial: "Commercial",
    }[p.type] || "Apartment"),
    status: p.status || "draft",
    possessionStatus: p.possessionStatus || "under_construction",
    reraVerified: !!p.reraVerified,
    zeroBrokerage: !!p.zeroBrokerage,
    reraId: p.rera || "",
    /* The stored date, not one rebuilt from "Dec 2027" -- that always came
       back as the 1st because the label never held a day. */
    possessionDate: p.possessionDate || possessionDateFromParts(p.possessionLabel, p.possessionYear),
    shortDescription: p.shortDescription || "",
    fullDescription: p.about || "",
    aboutHeading: p.aboutHeading || "",
    landParcel: p.landParcel || "", towers: p.towers || "", floors: p.floors || "",
    villaLength: p.villaLength || "", villaWidth: p.villaWidth || "",
    plotLength: p.plotLength || "", plotWidth: p.plotWidth || "",
    plotFloorsAllowed: p.plotFloorsAllowed || "", listing: p.listing || "",
    expectedRoi: p.expectedRoi || "",
    totalUnits: p.totalUnits ? String(p.totalUnits) : "",
    parking: p.parking || "", totalAmenities: p.totalAmenities || "",
    vastu: p.vastu || "", reraPossession: p.reraPossession || "", litigation: p.litigation || "", naPlot712: p.naPlot712 || "", gatedCommunity: p.gatedCommunity || "", servantRoom3plus: p.servantRoom3plus || "",
    coverImage: coverImg && coverImg.url ? { url: coverImg.url, name: "" } : null,
    gallery, floorPlans: [],
    configs,
    amenities,
    specialOffer: p.specialOffer || { enabled: false, badge: "", title: "", discount: "", description: "", validTill: "", youtubeUrl: "" },
    location: { address: p.address || "", lat: p.lat ?? "", lng: p.lng ?? "", mapEmbedUrl: p.mapEmbedUrl || "", nearby },
    brochureUrl: p.brochureUrl || "",
    walkthroughUrl: p.walkthroughUrl || "",
    walkthroughLabel: p.walkthroughLabel || "",
    floorPlanPdfUrl: p.floorPlanPdfUrl || "",
    seo: { metaTitle: "", metaDescription: "", focusKeyword: "" },
    id: p.id,
  };
}

export function PropertyWizard({ initial, onCancel, onSaved }) {
  const isEditing = !!initial;
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => (initial ? { ...emptyProperty(), ...fromUi(initial) } : emptyProperty()));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { setDirtyForm } = useAppData();
  const [dirty, setDirty] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  useEffect(() => () => setDirtyForm(false), [setDirtyForm]);
  const setDataDirty = (updater) => { setDirty(true); setDirtyForm(true); setData(updater); };
  const requestCancel = () => { if (dirty) { setConfirmingClose(true); } else { onCancel(); } };
  const confirmDiscard = () => { setDirtyForm(false); setConfirmingClose(false); onCancel(); };

  const Component = STEPS[step].component;
  const isLast = step === STEPS.length - 1;

  const validateBasic = () => {
    const e = {};
    if (!data.title.trim()) e.title = "Project title is required";
    if (!data.builderId) e.builderId = "Pick a builder";
    if (!data.city) e.city = "Pick a city";
    if (!data.localityId) e.localityId = "Pick a locality";
    return e;
  };
  const validateConfigs = () => {
    const e = {};
    const isPlot = data.propertyType === "Plot";
    const valid = (data.configs || []).filter((c) =>
      isPlot ? (c.price && c.carpetArea) : (c.bhk && c.price && c.carpetArea)
    );
    if (!valid.length) e.configs = isPlot
      ? "Add at least one complete configuration (area + price)"
      : "Add at least one complete configuration (type + area + price)";
    return e;
  };

  const goNext = () => {
    if (step === 0) {
      const e = validateBasic();
      if (Object.keys(e).length) { setErrors(e); toast("Please fix the highlighted fields"); return; }
      if (!data.slug) setData((d) => ({ ...d, slug: slugify(d.title) }));
    }
    setErrors({});
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const goPrev = () => { setErrors({}); setStep(Math.max(step - 1, 0)); };
  const goTo = (i) => { setErrors({}); setStep(i); };

  const buildRecord = (forceStatus) => ({
    ...data,
    slug: data.slug || slugify(data.title),
    status: forceStatus || data.status,
  });

  // Replace the property's image rows with the current cover + gallery.
  //
  // This used to only append. Every save re-attached the cover AND every
  // gallery image already loaded into state, so rows multiplied on each edit:
  // Skyline Vista accumulated 15 "cover" rows, Runwal Crown 10. loadFromProperty
  // takes the FIRST cover and drops the rest, so those extras became invisible
  // in the wizard while the customer site — which reads images[] without
  // filtering by category — showed all of them. That is the "site shows more
  // images than the admin" bug.
  //
  // Deleting first makes the save idempotent: what you see in the wizard is
  // exactly what ends up in the database. Existing rows are read from the
  // property record passed in, not from a re-fetch, so this only clears what
  // was already loaded.
  async function attachImages(propertyId, existingImages = []) {
    for (const img of existingImages) {
      if (img?.id) {
        try {
          await propertiesApi.detachImage(propertyId, img.id);
        } catch (e) {
          // Log rather than swallow. A bare catch here would hide a failing
          // endpoint and present as "images keep duplicating on save" with no
          // clue why — the same way an empty catch hid a typo'd method name in
          // openEdit. Keep going so one bad row does not abort the whole save.
          console.error("[properties] detach failed for image", img.id, e);
        }
      }
    }

    let order = 0;
    if (data.coverImage?.url) {
      await propertiesApi.attachImage(propertyId, { url: data.coverImage.url, category: "cover", sortOrder: order++ });
    }
    for (const g of data.gallery || []) {
      if (g?.url) await propertiesApi.attachImage(propertyId, { url: g.url, category: "gallery", sortOrder: order++ });
    }
  }

  // Upsert per-route SEO metadata for /projects/<slug>.
  async function upsertSeo(savedSlug) {
    const s = data.seo || {};
    const hasSeo = s.metaTitle || s.metaDescription;
    if (!hasSeo) return;
    try {
      await seoApi.upsert({
        url: `/projects/${savedSlug}`,
        type: "property",
        title: s.metaTitle || data.title || "",
        description: s.metaDescription || data.shortDescription || "",
        ogImage: data.coverImage?.url || "",
        keywords: s.focusKeyword ? [s.focusKeyword] : [],
      });
    } catch (err) {
      // SEO is non-blocking — surface but don't fail the whole save.
      console.error("SEO upsert failed", err);
      toast("Saved, but SEO metadata couldn't be updated");
    }
  }

  async function persist(record) {
    // Set thumbnail from the cover so the card/hero have an image even
    // before images[] is read.
    if (data.coverImage?.url) record.thumbnail = data.coverImage.url;
    let saved;
    if (isEditing) saved = await propertiesApi.update(initial.id, record);
    else saved = await propertiesApi.create(record);
    // Pass the images loaded when the wizard OPENED — those are the rows to
    // clear before re-attaching. Without this, existingImages defaults to []
    // and every save appends instead of replacing, doubling the rows.
    await attachImages(saved.id, isEditing ? (initial.images || []) : []);
    await upsertSeo(saved.slug);
    return saved;
  }

  const handleSaveDraft = async () => {
    if (!data.title.trim()) {
      setErrors({ title: "At least a project title is needed to save" });
      setStep(0);
      toast("Add a project title to save as draft");
      return;
    }
    setSaving(true);
    try {
      await persist(buildRecord("draft"));
      toast(`${data.title} saved as draft`);
      setDirty(false); setDirtyForm(false);
      onSaved?.();
    } catch (err) {
      toast(err?.message || "Failed to save draft");
    } finally { setSaving(false); }
  };

  const handlePublish = async () => {
    const all = { ...validateBasic(), ...validateConfigs() };
    if (Object.keys(all).length) {
      setErrors(all);
      if (Object.keys(validateBasic()).length) setStep(0);
      else setStep(1);
      toast("Publishing requires Basic Info + at least one configuration");
      return;
    }
    setSaving(true);
    try {
      await persist(buildRecord("published"));
      toast(`${data.title} ${isEditing ? "updated" : "published"}`);
      setDirty(false); setDirtyForm(false);
      onSaved?.();
    } catch (err) {
      toast(err?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <ConfirmDialog open={confirmingClose} title="Discard unsaved changes?" message="You have unsaved changes in this project. Leaving will discard them." confirmLabel="Discard & leave" onConfirm={confirmDiscard} onCancel={() => setConfirmingClose(false)} />
      <button onClick={requestCancel} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ChevronLeft className="w-4 h-4" />Cancel
      </button>

      <PageHeader accent="inventory"
        title={isEditing ? `Editing: ${initial.title || initial.name}` : "Add Project"}
        subtitle={`${STEPS.length}-step wizard`}
        actions={<Btn variant="secondary" onClick={handleSaveDraft} disabled={saving}><Save className="w-4 h-4" />Save draft</Btn>}
      />

      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <button key={s.id} onClick={() => goTo(i)} className="flex items-center gap-1 shrink-0">
            <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-semibold ${i < step ? "bg-green-600 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
              {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={`text-xs font-medium hidden sm:inline ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 mx-0.5" />}
          </button>
        ))}
      </div>

      <Card className="p-6 min-h-[340px]">
        <Component data={data} setData={setDataDirty} errors={errors} />
      </Card>

      <div className="flex items-center justify-between mt-4">
        <Btn variant="secondary" onClick={goPrev} className={step === 0 ? "invisible" : ""}>
          <ChevronLeft className="w-4 h-4" />Previous
        </Btn>
        {!isLast ? (
          <Btn variant="primary" onClick={goNext}>Next<ChevronRight className="w-4 h-4" /></Btn>
        ) : (
          <Btn variant="primary" onClick={handlePublish} disabled={saving}>
            <Check className="w-4 h-4" />{saving ? "Saving…" : "Save project"}
          </Btn>
        )}
      </div>
    </div>
  );
}
